import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const fallbackImage =
  'https://readdy.ai/api/search-image?query=cinematic%20abstract%20gradient%20background%20of%20deep%20blue%20waves%20with%20warm%20orange%20light%20streaks%20minimal%20modern%20elegant%20film%20texture%20high%20detail%20soft%20atmospheric%20composition&width=1600&height=900&seq=mv-default-hero&orientation=landscape';

export default function HeroCarousel() {
  const featured = useMemo(() => {
    const withBackdrop = movies.filter((m) => m.backdrop);
    return withBackdrop.slice(0, 5);
  }, []);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (featured.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [featured.length]);

  if (featured.length === 0) {
    return (
      <section className="relative flex h-[420px] w-full items-center justify-center overflow-hidden md:h-[600px]">
        <img src={fallbackImage} alt="伊人在线精选影视宣传图" className="absolute inset-0 h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        <p className="relative z-10 font-heading text-xl font-semibold text-background-50">
          暂无精选影视内容
        </p>
      </section>
    );
  }

  const goTo = (i: number) => setIndex((i + featured.length) % featured.length);

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[600px]">
      {featured.map((movie, i) => (
        <Link
          key={movie.id}
          to={`/detail/${movie.id}`}
          className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 焦点图`}
            title={`${movie.title} 伊人在线`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

          <div className="absolute inset-0 flex items-center">
            <div className="mx-auto flex w-full px-4 md:px-10">
              <div className="max-w-2xl animate-fade-up">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-accent-950">
                  <i className="ri-fire-fill"></i>
                  本站热播精选
                </span>
                <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 drop-shadow md:text-5xl">
                  {movie.title}
                </h2>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100 md:text-base">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span>{movie.genres.join(' / ')}</span>
                </div>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base line-clamp-3">
                  {movie.description}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400">
                  <i className="ri-information-line"></i>
                  查看详情
                </span>
              </div>
            </div>
          </div>
        </Link>
      ))}

      <button
        type="button"
        aria-label="上一部"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 transition-colors hover:bg-black/55 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一部"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 transition-colors hover:bg-black/55 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-0 z-20 flex w-full justify-center gap-2">
        {featured.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 部`}
            onClick={() => goTo(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}